// App.js
import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

import { useAuth } from './context/AuthContext';
import Sidebar from './components/Sidebar';
import Login from './components/login/Login';
import Landing from './components/landing/Landing';
import SkillEditor from "./components/transform/SkillEditor";
import TransformEditor from "./components/transform/TransformEditor";
import axios from 'axios';
import './App.css';
import CharacterEditor from "./components/character/CharacterEditor";
import {CharacterProvider} from "./components/character/CharacterProvider";
import EventManager from "./components/eventManager/EventManager";
import EventBrowser from "./components/eventManager/EventBrowser";
import { expectedApiVersion } from './version';

// Lazy-loaded so markdown-it (and the rulebook reader) only download on demand.
const Rulebook = lazy(() => import('./components/rulebook/Rulebook'));


const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

/**
 * Compare major.minor of two semver strings.
 * Returns true if they match, false otherwise.
 */
function isVersionCompatible(actual, expected) {
    if (!actual || !expected) return false;
    const [aMaj, aMin] = actual.split('.').map(Number);
    const [eMaj, eMin] = expected.split('.').map(Number);
    return aMaj === eMaj && aMin === eMin;
}

/**
 * Parse a player's first name out of their "first last" name field and
 * normalize capitalization (players often enter it all lowercase).
 */
function toFirstName(fullName) {
    if (!fullName) return null;
    const first = fullName.trim().split(/\s+/)[0];
    if (!first) return null;
    return first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
}

const App = () => {
    const { token, setToken } = useAuth();
    const [staffPermissions, setStaffPermissions] = useState(false);
    const [playerId, setPlayerId] = useState(null);
    const [firstName, setFirstName] = useState(null);
    const [characterName, setCharacterName] = useState(null);
    const [versionWarning, setVersionWarning] = useState(null);

    useEffect(() => {
        const fetchPermissions = async () => {
            if (!token) return;

            try {
                const response = await axios.get(`${API_BASE_URL}/verify`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setStaffPermissions(response.data.staffPermissions || false);
                if (response.data.playerId != null) setPlayerId(response.data.playerId);
            } catch (error) {
                console.error('Failed to fetch staff permissions:', error);
                setStaffPermissions(false);
            }
        };

        fetchPermissions();
    }, [token]);

    useEffect(() => {
        if (!token) return;

        try {
            const [, payload] = token.split('.');
            const decoded = JSON.parse(atob(payload));
            if (decoded.exp && Date.now() >= decoded.exp * 1000) {
                setToken(null);
                setStaffPermissions(false);
                console.warn('Token expired');
            } else if (decoded.playerId != null) {
                setPlayerId(decoded.playerId);
            }
        } catch (err) {
            console.error('Failed to parse token:', err);
            setToken(null);
            setStaffPermissions(false);
        }
    }, [token, setToken]);

    // Resolve the logged-in player's first name and primary character name for the sidebar.
    useEffect(() => {
        if (!token || playerId == null) {
            setFirstName(null);
            setCharacterName(null);
            return;
        }
        const headers = { Authorization: `Bearer ${token}` };

        (async () => {
            try {
                const res = await axios.get(`${API_BASE_URL}/player/${playerId}`, { headers });
                setFirstName(toFirstName(res.data?.name));
            } catch {
                setFirstName(null);
            }
        })();

        // Scope to this player's characters (staff would otherwise get every character).
        // For now, use the first character returned; secondary/last-played logic comes later.
        (async () => {
            try {
                const res = await axios.get(`${API_BASE_URL}/character?playerId=${playerId}`, { headers });
                const list = Array.isArray(res.data) ? res.data : [];
                setCharacterName(list[0]?.name || null);
            } catch {
                setCharacterName(null);
            }
        })();
    }, [token, playerId]);

    // Check API version compatibility on mount
    useEffect(() => {
        (async () => {
            try {
                const res = await axios.get(`${API_BASE_URL}/version`);
                const apiVersion = res.data?.version;
                if (!apiVersion) {
                    setVersionWarning('Could not determine API version.');
                } else if (!isVersionCompatible(apiVersion, expectedApiVersion)) {
                    setVersionWarning(
                        `API version mismatch: server is v${apiVersion}, this app expects v${expectedApiVersion}.x. Some features may not work correctly.`
                    );
                } else {
                    setVersionWarning(null);
                }
            } catch {
                setVersionWarning('Could not connect to API to verify version.');
            }
        })();
    }, []);

    const handleLogout = () => {
        setToken(null);
        setStaffPermissions(false);
        setPlayerId(null);
        setFirstName(null);
        setCharacterName(null);
    };

    return (
        <div className="app-container">
            {versionWarning && (
                <div className="version-warning-banner">
                    ⚠ {versionWarning}
                    <button className="version-warning-dismiss" onClick={() => setVersionWarning(null)}>✕</button>
                </div>
            )}
            <Router>
                <div className="main-layout">
                    <div className="sidebar-container">
                        <Sidebar
                            staffPermissions={staffPermissions}
                            token={token}
                            characterName={characterName}
                            firstName={firstName}
                            onLogout={handleLogout}
                        />
                    </div>
                    <div className="content-area">
                        <Suspense fallback={<div style={{ padding: '2rem', color: '#9a9385' }}>Loading…</div>}>
                        <Routes>
                            <Route path="/" element={<Landing token={token} characterName={characterName} firstName={firstName} />} />
                            <Route path="/login" element={<Login />} />
                            <Route path="/schedule" element={<EventBrowser token={token}/>} />
                            <Route path="/rulebook" element={<Rulebook />} />

                            {token ? (
                                <>
                                    <Route path="/character" element={
                                        <CharacterProvider>
                                            <CharacterEditor token={token} playerId={playerId} staffPermissions={staffPermissions} />
                                        </CharacterProvider>
                                    } />
                                    <Route path="/character/:slug" element={
                                        <CharacterProvider>
                                            <CharacterEditor token={token} playerId={playerId} staffPermissions={staffPermissions} />
                                        </CharacterProvider>
                                    } />
                                    <Route path="/skills" element={<SkillEditor token={token}/>} />
                                    <Route path="/transform" element={staffPermissions ? <TransformEditor token={token}/> : <Navigate to="/" replace />} />
                                    <Route path="/transform/:slug" element={staffPermissions ? <TransformEditor token={token}/> : <Navigate to="/" replace />} />
                                    <Route path="/event-manager" element={<EventManager token={token}/>} />
                                    <Route path="*" element={<Navigate to="/" replace />} />
                                </>
                            ) : (
                                <Route path="*" element={<Navigate to="/" replace />} />
                            )}
                        </Routes>
                        </Suspense>
                    </div>
                </div>
            </Router>
        </div>
    );
};

export default App;
